import {
  CREATE_HUB_CATALOG,
  DELETE_HUB_CATALOG,
  FETCH_HUB_CATALOG_ADDONS,
  FETCH_HUB_CATALOG_BY_ID,
  FETCH_HUB_CATALOG_BY_ID_AND_VERSION,
  FETCH_HUB_CATALOGS,
  FETCH_HUB_CATALOGS_BY_TYPE,
  SEARCH_HUB_CATALOGS,
  UPDATE_HUB_CATALOG,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubCatalog } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { HUB_REVIEWS_KEY } from "./HubReviews";

export const HUB_CATALOGS_KEY = "hubCatalogsKey";
export const HUB_CATALOG_BY_ID_KEY = "hubCatalogByIdKey";
export const HUB_CATALOG_BY_ID_AND_VERSION_KEY = "hubCatalogByIdAndVersionKey";
export const HUB_CATALOGS_BY_TYPE_KEY = "hubCatalogsByTypeKey";
export const HUB_CATALOG_ADDONS_KEY = "hubCatalogAddonsKey";

export const getHubCatalogsKey = () => [HUB_CATALOGS_KEY] as const;

export const getHubCatalogByIdKey = (catalogId: number) =>
  [HUB_CATALOG_BY_ID_KEY, catalogId] as const;

export const getHubCatalogByIdAndVersionKey = (
  catalogId: number,
  version: string | number,
) => [HUB_CATALOG_BY_ID_AND_VERSION_KEY, catalogId, version] as const;

export const getHubCatalogsByTypeKey = (typeId: number) =>
  [HUB_CATALOGS_BY_TYPE_KEY, typeId] as const;

export const getHubCatalogAddonsKey = (parentCatalogId: number) =>
  [HUB_CATALOG_ADDONS_KEY, parentCatalogId] as const;

export const useHubCatalogs = (
  options?: Partial<
    UseQueryOptions<
      IHubCatalog[],
      AxiosError,
      IHubCatalog[],
      ReturnType<typeof getHubCatalogsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogsKey(),
    queryFn: async () => {
      return mutationFetch<IHubCatalog[]>({
        url: FETCH_HUB_CATALOGS,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubCatalog = (
  catalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCatalog,
      AxiosError,
      IHubCatalog,
      ReturnType<typeof getHubCatalogByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogByIdKey(catalogId),
    queryFn: async () => {
      return mutationFetch<IHubCatalog>({
        url: FETCH_HUB_CATALOG_BY_ID.replace(":catalogId", String(catalogId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && catalogId > 0,
  });
};

export const useHubCatalogByVersion = (
  catalogId: number,
  version: string | number,
  options?: Partial<
    UseQueryOptions<
      IHubCatalog,
      AxiosError,
      IHubCatalog,
      ReturnType<typeof getHubCatalogByIdAndVersionKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogByIdAndVersionKey(catalogId, version),
    queryFn: async () => {
      return mutationFetch<IHubCatalog>({
        url: FETCH_HUB_CATALOG_BY_ID_AND_VERSION.replace(
          ":catalogId",
          String(catalogId),
        ).replace(":version", String(version)),
        method: "GET",
      });
    },
    ...options,
    enabled:
      (options?.enabled ?? true) &&
      catalogId > 0 &&
      version !== undefined &&
      version !== null &&
      version !== "",
  });
};

export const useHubCatalogsByIds = (
  catalogIds: { catalogId: number; version: string | number }[],
  options?: { enabled?: boolean },
) => {
  const enabled = options?.enabled ?? true;
  const queries = useQueries({
    queries: catalogIds.map(({ catalogId, version }) => ({
      queryKey: getHubCatalogByIdAndVersionKey(catalogId, version),
      queryFn: async () => {
        return mutationFetch<IHubCatalog>({
          url: FETCH_HUB_CATALOG_BY_ID_AND_VERSION.replace(
            ":catalogId",
            String(catalogId),
          ).replace(":version", String(version)),
          method: "GET",
        });
      },
      enabled:
        enabled &&
        catalogId > 0 &&
        version !== undefined &&
        version !== null &&
        version !== "",
    })),
  });

  const data = queries
    .map((q) => q.data)
    .filter((c): c is IHubCatalog => c != null);

  const isLoading =
    enabled &&
    catalogIds.some((item) => item.catalogId > 0) &&
    queries.some((q) => q.isLoading);

  return { data, isLoading };
};

export const useHubCatalogsByType = (
  typeId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCatalog[],
      AxiosError,
      IHubCatalog[],
      ReturnType<typeof getHubCatalogsByTypeKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogsByTypeKey(typeId),
    queryFn: async () => {
      return mutationFetch<IHubCatalog[]>({
        url: FETCH_HUB_CATALOGS_BY_TYPE.replace(":typeId", String(typeId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && typeId > 0,
  });
};

export const useHubCatalogAddons = (
  parentCatalogId: number,
  options?: Partial<
    UseQueryOptions<
      IHubCatalog[],
      AxiosError,
      IHubCatalog[],
      ReturnType<typeof getHubCatalogAddonsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubCatalogAddonsKey(parentCatalogId),
    queryFn: async () => {
      return mutationFetch<IHubCatalog[]>({
        url: FETCH_HUB_CATALOG_ADDONS.replace(
          ":catalogId",
          String(parentCatalogId),
        ),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && parentCatalogId > 0,
  });
};

const createHubCatalog = (body: IHubCatalog): Promise<IHubCatalog> => {
  return mutationFetch<IHubCatalog>({
    url: CREATE_HUB_CATALOG,
    method: "POST",
    body,
  });
};

const updateHubCatalog = (body: IHubCatalog): Promise<IHubCatalog> => {
  return mutationFetch<IHubCatalog>({
    url: UPDATE_HUB_CATALOG,
    method: "PUT",
    body,
  });
};

const deleteHubCatalog = ({
  catalogId,
  versionId,
}: {
  catalogId: number;
  versionId: string;
}): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_HUB_CATALOG.replace(":catalogId", String(catalogId)).replace(
      ":versionId",
      versionId,
    ),
    method: "DELETE",
  });
};

export const useCreateHubCatalog = (
  options?: UseMutationOptions<IHubCatalog, AxiosError, IHubCatalog>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubCatalog,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_ADDONS_KEY] });
      if (data.catalogId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOGS_BY_TYPE_KEY],
      });
    },
    ...options,
  });
};

export const useUpdateHubCatalog = (
  options?: UseMutationOptions<IHubCatalog, AxiosError, IHubCatalog>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHubCatalog,
    ...options,
    onSuccess: (...args) => {
      const [data] = args;

      queryClient.setQueryData<IHubCatalog[]>(getHubCatalogsKey(), (old) => {
        if (!old) return old;
        return old.map((c) => (c.catalogId === data.catalogId ? data : c));
      });

      if (data.typeId != null) {
        queryClient.setQueryData<IHubCatalog[]>(
          getHubCatalogsByTypeKey(data.typeId),
          (old) => {
            if (!old) return old;
            return old.map((c) => (c.catalogId === data.catalogId ? data : c));
          },
        );
      }

      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_REVIEWS_KEY] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_ADDONS_KEY] });
      if (data.catalogId != null) {
        queryClient.setQueryData<IHubCatalog>(
          getHubCatalogByIdKey(data.catalogId),
          data,
        );
        queryClient.invalidateQueries({
          queryKey: getHubCatalogByIdKey(data.catalogId),
        });
        queryClient.invalidateQueries({
          queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, data.catalogId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOGS_BY_TYPE_KEY],
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteHubCatalog = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    { catalogId: number; versionId: string }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubCatalog,
    onSuccess: (_, { catalogId }) => {
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_ADDONS_KEY] });
      queryClient.invalidateQueries({
        queryKey: getHubCatalogByIdKey(catalogId),
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY, catalogId],
      });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOGS_BY_TYPE_KEY],
      });
    },
    ...options,
  });
};

export const useSearchHubCatalogs = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubCatalog>, "url" | "payload">,
) => {
  return useSearchQuery<IHubCatalog>({
    url: SEARCH_HUB_CATALOGS,
    payload,
    ...options,
  });
};
